import {
  IconBuildingWarehouse,
  IconCashBanknote,
  IconCoins,
  IconSquareRoundedPercentage,
  IconVs,
} from '@tabler/icons-react'
import ReactEcharts from 'echarts-for-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ContentCard,
  Leaderboard,
  Loading,
  MetricCard,
  ScalrBoxView,
  ScalrCarousel,
} from '@/components/customComponents'
import { MasterChart } from '@/components/ui/charts'
import { DateRangeValidator } from '@/components/ui/charts/DateRangeValidator'
import {
  RadioGroupButton,
  RadioGroupButtonItem,
} from '@/components/ui/RadioGroup'

import { mockDataHandler } from '@/api/clarity/mockSpend/mockDatahandler'
import { chartColors, metricCardColors } from '@/constants/COLOR_PALETTE'
import { useSpendMetrics, useSpendRegionBoxMetrics } from '@/hooks'
import {
  selectPageRefreshTrigger,
  selectTenant,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { KpiGoalsModel } from '@/types'
import { dateToString, type DateType } from '@/utils'

export default function SpendPage({
  kpiGoals,
  kpiGoalsLoading,
}: {
  kpiGoals: KpiGoalsModel
  kpiGoalsLoading: boolean
}) {
  const { t } = useTranslation()
  const tenant = useAppSelector(selectTenant)
  const refreshTrigger = useAppSelector(selectPageRefreshTrigger('spend'))

  const { selectedBranch, date, dateFilterType } = useReduxBaseFilter('spend')
  const [selected, setSelected] = useState<'category' | 'supplier'>('supplier')

  const handleSelectedChange = (value: 'category' | 'supplier') => {
    setSelected(value)
  }

  const [mfrSpendRebateData, setMfrSpendRebateData] = useState<any>(null)
  const [mockDataCpiPti, setMockDataCpiPti] = useState<any>(null)

  const fetchMockData = async () => {
    const mockData = await mockDataHandler(
      selectedBranch,
      dateToString(date, 'from', 'yyyy-mm'),
      dateToString(date, 'to', 'yyyy-mm')
    )
    configureRebateTrackingData(mockData.mfrSpendRebate)
    const mockDataCpiPti = generateMonthlyMockData(
      dateToString(date, 'from'),
      dateToString(date, 'to')
    )
    setMockDataCpiPti(mockDataCpiPti)
  }

  useEffect(() => {
    try {
      fetchMockData()
    } catch (error) {}
  }, [date, selectedBranch])

  const configureRebateTrackingData = (rebateData: any) => {
    const rebateTrackingData = rebateData.map((item: any) => ({
      id: item.supplierName,
      supplierName: item.supplierName,
      spend: item.spend.toFixed(2),
      rebatesAmount: item.rebate.toFixed(2),
      rebatesPercentage: ((item.rebate / item.spend) * 100).toFixed(2),
    }))

    setMfrSpendRebateData(rebateTrackingData.slice(0, 5))
  }

  const {
    spendMetrics,
    metricCardData,
    categoryPtiData,
    categorySpendData,
    supplierSpendData,
    supplierPtiData,
    categoryPtiLeaderboardColumns,
    supplierPtiLeaderboardColumns,
    categorySpendLeaderboardColumns,
    supplierSpendLeaderboardColumns,
    categoryPieChartData,
    supplierPieChartData,
    materialCodeDotChartOptions,
    rebateTrackingColumns,
    rebateTrackingData,
    loading,
    refetch,
  } = useSpendMetrics(
    dateToString(date, 'from', 'yyyy-mm'),
    dateToString(date, 'to', 'yyyy-mm'),
    dateFilterType as DateType,
    selectedBranch,
    // temp for dev tenant
    tenant as string
  )

  const {
    processedData,
    loading: regionCardsLoading,
    refetch: regionCardsRefetch,
  } = useSpendRegionBoxMetrics(
    dateToString(date, 'from', 'yyyy-mm'),
    dateToString(date, 'to', 'yyyy-mm'),
    dateFilterType as DateType,
    tenant
  )

  // Watch for refresh trigger from Redux
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing spend page data...')
      refetch()
      regionCardsRefetch()
    }
  }, [refreshTrigger, refetch, regionCardsRefetch])

  return (
    <div className="space-y-6">
      <div className="space-y-3 mb-10">
        <ScalrCarousel>
          <MetricCard
            title={t('clarity.spend.cogsPercentage')}
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.summary?.cogsPercentage
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.summary?.cogsPercentage
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.summary?.cogsPercentage
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.summary?.cogsPercentage
            }
            content={spendMetrics?.summary?.cogsPercentage}
            type="percentage"
            kpiGoal={kpiGoals?.spend?.cogsPercentage}
            loading={loading || kpiGoalsLoading}
            icon={<IconCashBanknote color="white" size={20} />}
            color={metricCardColors.blue500}
            isIncreasingPositive={false}
            quarterToDate
          />
          <MetricCard
            title={t('clarity.spend.ptiImpact')}
            mtdCurrContent={metricCardData?.currMetricsMonthly?.summary?.impact}
            ytdCurrContent={metricCardData?.currMetricsYearly?.summary?.impact}
            mtdPrevContent={metricCardData?.prevMetricsMonthly?.summary?.impact}
            ytdPrevContent={metricCardData?.prevMetricsYearly?.summary?.impact}
            content={spendMetrics?.summary?.impact}
            type="money"
            kpiGoal={kpiGoals?.spend?.ptiImpact}
            loading={loading || kpiGoalsLoading}
            icon={<IconCoins color="white" size={20} />}
            color={metricCardColors.red500}
            isIncreasingPositive={false}
            quarterToDate
          />
          <MetricCard
            title={t('clarity.spend.compliancePercentage')}
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.summary
                ?.spendCompliancePercentage
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.summary
                ?.spendCompliancePercentage
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.summary
                ?.spendCompliancePercentage
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.summary
                ?.spendCompliancePercentage
            }
            content={spendMetrics?.summary?.spendCompliancePercentage}
            type="percentage"
            kpiGoal={kpiGoals?.spend?.compliancePercentage}
            loading={loading || kpiGoalsLoading}
            icon={<IconSquareRoundedPercentage color="white" size={20} />}
            color={metricCardColors.green500}
            quarterToDate
          />
          <MetricCard
            title={t('clarity.spend.cogsVsTheoretical')}
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.summary?.cogsVsTheoretical
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.summary?.cogsVsTheoretical
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.summary?.cogsVsTheoretical
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.summary?.cogsVsTheoretical
            }
            content={spendMetrics?.summary?.cogsVsTheoretical}
            type="percentage"
            kpiGoal={kpiGoals?.spend?.cogsVsTheoretical}
            loading={loading || kpiGoalsLoading}
            icon={<IconVs color="white" size={20} />}
            color={metricCardColors.orange500}
            isIncreasingPositive={false}
            quarterToDate
          />
          <MetricCard
            title={t('clarity.spend.inventoryValue')}
            mtdCurrContent={
              metricCardData?.currMetricsMonthly?.summary?.inventoryValue
            }
            ytdCurrContent={
              metricCardData?.currMetricsYearly?.summary?.inventoryValue
            }
            mtdPrevContent={
              metricCardData?.prevMetricsMonthly?.summary?.inventoryValue
            }
            ytdPrevContent={
              metricCardData?.prevMetricsYearly?.summary?.inventoryValue
            }
            content={spendMetrics?.summary?.inventoryValue}
            type="money"
            kpiGoal={kpiGoals?.spend?.inventoryValue}
            loading={loading || kpiGoalsLoading}
            icon={<IconBuildingWarehouse color="white" size={20} />}
            color={metricCardColors.blue300}
            quarterToDate
          />
        </ScalrCarousel>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="leading-none tracking-tight text-black-500 text-display-s">
            {selected === 'supplier'
              ? t('clarity.spend.supplier')
              : t('clarity.spend.category')}{' '}
            {t('clarity.spend.leaderboard')}
          </span>
          <div>
            <RadioGroupButton
              defaultValue="supplier"
              onValueChange={handleSelectedChange}
            >
              <RadioGroupButtonItem
                value="supplier"
                className="items-center justify-center inline-flex"
              >
                {t('clarity.spend.supplier')}
              </RadioGroupButtonItem>
              <RadioGroupButtonItem
                value="category"
                className="items-center justify-center inline-flex"
              >
                {t('clarity.spend.category')}
              </RadioGroupButtonItem>
            </RadioGroupButton>
          </div>
        </div>

        {loading ? (
          <Loading />
        ) : (
          <>
            {selected === 'category' && (
              <div className="grid grid-cols-3 gap-4">
                <Leaderboard
                  title={t('clarity.spend.spendTitle')}
                  data={categorySpendData}
                  columns={categorySpendLeaderboardColumns}
                  loading={loading}
                  showBadge={false}
                  sortBy="change"
                />
                <Leaderboard
                  title={t('clarity.spend.pti')}
                  data={categoryPtiData}
                  columns={categoryPtiLeaderboardColumns}
                  loading={loading}
                  showBadge={false}
                  sortBy="impact"
                  sortOrder="asc"
                />
                <ContentCard
                  title={`${t('clarity.spend.category')} ${t('clarity.spend.mix')}`}
                  loading={!categoryPieChartData}
                  isEmpty={!categoryPieChartData}
                >
                  <ReactEcharts option={categoryPieChartData} />
                </ContentCard>
              </div>
            )}
            {selected === 'supplier' && (
              <div className="grid grid-cols-3 gap-4">
                <Leaderboard
                  title={t('clarity.spend.spendTitle')}
                  data={supplierSpendData}
                  columns={supplierSpendLeaderboardColumns}
                  loading={false}
                  showBadge={false}
                  truncate
                />
                <Leaderboard
                  title={t('clarity.spend.pti')}
                  data={supplierPtiData}
                  columns={supplierPtiLeaderboardColumns}
                  loading={false}
                  showBadge={false}
                  sortBy="impact"
                  sortOrder="asc"
                  truncate
                />
                <ContentCard
                  title={`${t('clarity.spend.supplier')} ${t('clarity.spend.mix')}`}
                  loading={!supplierPieChartData}
                  isEmpty={!supplierPieChartData}
                >
                  <ReactEcharts option={supplierPieChartData} />
                </ContentCard>
              </div>
            )}
          </>
        )}
      </div>

      <ScalrBoxView
        data={processedData}
        loading={regionCardsLoading}
        filterValues={{
          start: dateToString(date, 'from'),
          end: dateToString(date, 'to'),
        }}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ContentCard
          title={t('clarity.spend.materialCodeDeviation')}
          loading={loading}
          isEmpty={!materialCodeDotChartOptions}
        >
          {materialCodeDotChartOptions && (
            <ReactEcharts option={materialCodeDotChartOptions} />
          )}
        </ContentCard>

        <Leaderboard
          title={t('clarity.spend.rebateTracking')}
          data={rebateTrackingData}
          columns={rebateTrackingColumns}
          loading={loading}
          showCard
          sortBy="totalRebate"
        />

        <ContentCard
          title={t('clarity.spend.ptiVsCpi')}
          loading={loading}
          isEmpty={!mockDataCpiPti}
          isBeta
        >
          {mockDataCpiPti && (
            <ReactEcharts option={getPTICPILineChartData(mockDataCpiPti)} />
          )}
        </ContentCard>
      </div>

      <DateRangeValidator
        dateRange={date}
        chartType="spend chart"
        chartTitle="Spend Chart"
      >
        <MasterChart
          chartType="spend"
          dateRange={date}
          selectedBranchFilter={selectedBranch}
          chartTitle={t('clarity.charts.spendChart')}
        />
      </DateRangeValidator>
    </div>
  )
}

function hashDateToRandom(date: string, seedOffset: number): number {
  const str = date + seedOffset
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash % 10000) / 10000
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`
}

function generateMonthlyMockData(startDate: string, endDate: string) {
  const result = []
  const start = new Date(startDate)
  const end = new Date(endDate)

  // Set start date to one year ago
  const oneYearAgo = new Date(start)
  oneYearAgo.setFullYear(start.getFullYear() - 1)

  const current = new Date(oneYearAgo.getFullYear(), oneYearAgo.getMonth(), 1)
  while (current <= end) {
    const dateStr = current.toISOString().split('T')[0].slice(0, 7) // YYYY-MM

    const coffeeCPI = formatPercent(hashDateToRandom(dateStr, 1) * 1.2)
    const coffeePTI = formatPercent(hashDateToRandom(dateStr, 2) * 4.0)
    const dairyCPI = formatPercent(hashDateToRandom(dateStr, 3) * 0.9)
    const dairyPTI = formatPercent(hashDateToRandom(dateStr, 4) * 3.0)

    result.push({
      month: dateStr,
      coffeeCPI,
      coffeePTI,
      dairyCPI,
      dairyPTI,
    })

    // Move to next month
    current.setMonth(current.getMonth() + 1)
  }

  return result
}

const getPTICPILineChartData = (mockData: any) => {
  const rawData = mockData.map((item: any) => ({
    coffeePTI: parseFloat(item.coffeePTI),
    coffeeCPI: parseFloat(item.coffeeCPI),
    // dairyPTI: parseFloat(item.dairyPTI),
    // dairyCPI: parseFloat(item.dairyCPI),
  }))

  return {
    legend: {
      selectedMode: false,
      orient: 'horizontal',
      top: '0%',
      left: 'center',
      data: ['PTI', 'CPI'],
      icon: 'circle',
      itemGap: 20,
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params: any) {
        return `${params[0].marker}${params[0].seriesName}: ${params[0].value}%`
      },
    },
    grid: {
      bottom: '20%',
      left: '12%',
      right: '2%',
    },
    xAxis: {
      type: 'category',
      data: mockData.map((item: any) => {
        return item.month
      }),
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: '{value}%',
      },
    },
    series: [
      {
        name: 'PTI',
        type: 'line',
        data: rawData.map((item: any) => item.coffeePTI),
        itemStyle: {
          color: chartColors[0],
        },
        lineStyle: {
          color: chartColors[0],
          width: 2,
        },
        symbol: 'circle',
        symbolSize: 6,
      },
      {
        name: 'CPI',
        type: 'line',
        data: rawData.map((item: any) => item.coffeeCPI),
        itemStyle: {
          color: chartColors[1],
        },
        lineStyle: {
          color: chartColors[1],
          width: 2,
        },
        symbol: 'circle',
        symbolSize: 6,
      },
    ],
  }
}
